/*
 * Reusable API scheduler with per-entry caching (with TTL), a manual force-refresh
 * bypass, and cooperative cancellation. Extracted from the Facility Dashboard
 * (Facdash.vue) so any screen can get the same behavior -- cache reuse, batched
 * execution (parallel within a batch, batches run one after another), failed-only
 * single retry, and stale-response protection -- without duplicating the logic.
 *
 * Each screen should create its OWN scheduler via createApiRequestScheduler() at
 * module scope (not inside a component instance) so its cache survives the owning
 * component being destroyed/recreated on navigation, while staying isolated from
 * other screens' schedulers/caches.
 *
 * This module knows nothing about Vue, axios, or any specific screen's config
 * (batch size, TTLs, endpoints) -- all of that stays with the caller.
 *
 * Usage sketch:
 *   import axios from 'axios'
 *   import { createApiRequestScheduler } from '@/utils/apiRequestScheduler'
 *
 *   var scheduler = createApiRequestScheduler({
 *     isCancel: axios.isCancel,
 *     cancelTokenSource: function () { return axios.CancelToken.source() }
 *   })
 *
 *   function loadSomething () {
 *     var params = { comm_id: commId, from_dt: fromDt, to_dt: toDt }
 *     return scheduler.fetchWithCache({
 *       key: scheduler.buildCacheKey('loadSomething', params),
 *       ttlMs: isLive ? 5 * 60 * 1000 : null, // null = cache indefinitely
 *       request: function (cancelToken) {
 *         return axios.get(url, { params: params, cancelToken: cancelToken })
 *       },
 *       onSuccess: function (response) { ...apply response... },
 *       onError: function (err) { ...show error... }
 *     })
 *   }
 *
 *   // on a filter/selection change:
 *   scheduler.invalidateCurrentRun('selection changed')
 *   // on leaving the screen:
 *   scheduler.invalidateCurrentRun('screen left')
 *   // to force-bypass the cache for the next run (manual refresh):
 *   scheduler.setForceRefresh(true)
 *   // batchSize: 3 -> up to 3 requests in flight at once, batches run one after another
 *   scheduler.runQueue(tasks, { batchSize: 3, onFailedApisChange: fn })
 */

function chunkArray (arr, size) {
  var chunks = []
  var step = size > 0 ? size : arr.length
  for (var i = 0; i < arr.length; i += step) {
    chunks.push(arr.slice(i, i + step))
  }
  return chunks
}

export function createApiRequestScheduler (options) {
  options = options || {}
  var isCancelFn = options.isCancel || function () { return false }
  var makeCancelSource = options.cancelTokenSource || function () {
    return { token: undefined, cancel: function () {} }
  }

  var cache = {}
  var state = {
    runToken: 0,
    forceRefreshActive: false,
    // up to `batchSize` requests can be in flight at once now that a batch runs its tasks in
    // parallel, so this tracks all of them (not just one) for invalidateCurrentRun to cancel
    activeCancelSources: new Set()
  }

  function buildCacheKey (name, params) {
    return name + '::' + JSON.stringify(params || {})
  }

  // Fetches one request with caching, TTL, force-refresh bypass, de-duplication of concurrent
  // identical requests, and cooperative staleness protection. `request(cancelToken)` must
  // return a Promise (e.g. an axios call configured with { cancelToken }). A response that
  // resolves/rejects after this call's run has been superseded (invalidateCurrentRun was
  // called since) is dropped -- neither onSuccess nor onError runs for it, and the rejection
  // it produces is NOT treated as a normal failure by runQueue.
  function fetchWithCache (opts) {
    var key = opts.key
    var ttlMs = opts.ttlMs
    var request = opts.request
    var onSuccess = opts.onSuccess
    var onError = opts.onError
    var issuedRunToken = state.runToken

    if (state.forceRefreshActive) {
      delete cache[key]
    }
    var entry = cache[key]
    if (entry && entry.status === 'success' && entry.expiresAt !== null && Date.now() >= entry.expiresAt) {
      // TTL expired for this entry only -- other still-valid entries are unaffected
      delete cache[key]
      entry = null
    }
    if (!entry) {
      entry = { status: 'pending', promise: null, response: null, expiresAt: null }
      cache[key] = entry
    }

    var responsePromise
    if (entry.status === 'success') {
      responsePromise = Promise.resolve(entry.response)
    } else {
      if (!entry.promise) {
        var source = makeCancelSource()
        state.activeCancelSources.add(source)
        entry.promise = request(source.token)
          .then(function (response) {
            state.activeCancelSources.delete(source)
            entry.status = 'success'
            entry.response = response
            // null/undefined ttlMs = cache indefinitely; a real number (0 included) is a
            // genuine expiry offset -- don't let a falsy-but-valid 0 be read as "no TTL"
            entry.expiresAt = (typeof ttlMs === 'number') ? Date.now() + ttlMs : null
            return response
          })
          .catch(function (err) {
            state.activeCancelSources.delete(source)
            // don't let a failed/cancelled request poison the cache for a future retry
            delete cache[key]
            throw err
          })
      }
      responsePromise = entry.promise
    }

    return responsePromise.then(function (response) {
      if (state.runToken !== issuedRunToken) {
        return // superseded -- drop the response instead of applying stale state
      }
      return onSuccess(response)
    }, function (err) {
      if (isCancelFn(err)) {
        throw err
      }
      if (state.runToken !== issuedRunToken) {
        throw err // superseded -- don't surface a stale error for an old selection
      }
      if (onError) {
        onError(err)
      }
      throw err
    })
  }

  // Runs `tasks` (an array of { name, run }, `run` returning a Promise) in batches of
  // `batchSize`: every task within a batch runs concurrently, but the next batch only starts
  // once the whole current batch has settled. A task that throws is recorded in failedApis and
  // its batch-mates and later batches still proceed; after the full pass, failed tasks are
  // retried once, in the same batched fashion. A cancelled task, or one whose result arrived
  // after this run was superseded, is neither recorded as failed nor retried, and the run stops
  // (no further batches are started). `onFailedApisChange` (optional) is called with a snapshot
  // array every time the failed list changes, so a caller can mirror it into reactive state.
  async function runQueue (tasks, opts) {
    opts = opts || {}
    var batchSize = opts.batchSize || tasks.length
    var log = opts.log !== false
    var onFailedApisChange = opts.onFailedApisChange || function () {}
    var runToken = ++state.runToken
    var failed = []
    onFailedApisChange(failed.map(function (t) { return t.name }))

    // A task's own run being superseded is what "stale" means here -- it's always signalled by
    // a run-token mismatch, since invalidateCurrentRun bumps the token in the same call that
    // cancels in-flight requests. Deliberately NOT also treating every Cancel as stale: two
    // different runs can share one in-flight request for the same cache key (see
    // fetchWithCache's de-duplication), so a Cancel raised by an older, already-superseded run
    // can reach a waiter belonging to the CURRENT, still-valid run. If we treated any Cancel as
    // automatically stale, that current run would silently stop instead of retrying the task --
    // which is exactly the "cancelled request" bug this function must avoid.
    function isStale () {
      return state.runToken !== runToken
    }

    // Runs one task, converting a rejection into a { task, ok: false, ... } result instead of
    // letting it reject -- so Promise.all over a batch never short-circuits on the first
    // failure, and every task in the batch gets to finish (or fail) independently.
    function runTask (task) {
      return task.run().then(
        function () {
          return { task: task, ok: true }
        },
        function () {
          // classify purely by staleness (see isStale's note above), not by whether this
          // particular error happens to be a Cancel
          return { task: task, ok: false, cancelled: isStale() }
        }
      )
    }

    // Runs one batch's tasks in parallel (batchSize = max concurrency), waits for all of them
    // to settle, and returns the ones that genuinely failed (excluding cancelled/stale ones).
    async function runBatch (batchTasks) {
      var results = await Promise.all(batchTasks.map(runTask))
      var stillFailed = []
      results.forEach(function (result) {
        if (!result.ok && !result.cancelled) {
          stillFailed.push(result.task)
        }
      })
      return stillFailed
    }

    async function runInBatches (taskList, label) {
      var batches = chunkArray(taskList, batchSize)
      for (var b = 0; b < batches.length; b++) {
        if (isStale()) {
          return
        }
        if (log) {
          console.log('[ApiScheduler] ' + label + ' batch ' + (b + 1) + '/' + batches.length + ': ' +
            batches[b].map(function (t) { return t.name }).join(', '))
        }
        var batchFailed = await runBatch(batches[b])
        if (isStale()) {
          return
        }
        if (batchFailed.length > 0) {
          failed = failed.concat(batchFailed)
          onFailedApisChange(failed.map(function (t) { return t.name }))
        }
      }
    }

    try {
      await runInBatches(tasks, 'First pass')
      if (isStale()) {
        return
      }
      if (failed.length === 0) {
        if (log) console.log('[ApiScheduler] First pass completed, all succeeded')
        return
      }
      if (log) console.log('[ApiScheduler] First pass completed, retrying failed: ' + failed.map(function (t) { return t.name }).join(', '))
      var toRetry = failed
      failed = []
      onFailedApisChange(failed.map(function (t) { return t.name }))
      await runInBatches(toRetry, 'Retry')
      if (!isStale() && failed.length > 0 && log) {
        console.warn('[ApiScheduler] Still failing after retry: ' + failed.map(function (t) { return t.name }).join(', '))
      }
    } finally {
      // only the run that still owns the token clears the one-shot refresh flag -- otherwise a
      // superseded run finishing late could clear it out from under a newer one
      if (state.runToken === runToken) {
        state.forceRefreshActive = false
      }
    }
  }

  // Invalidates whatever run is currently in flight (a new selection was made, or the owning
  // screen is being left): bumps the run token so in-progress/queued work stops at its next
  // check, and cancels every currently-in-flight request (a batch can have up to `batchSize` of
  // them at once). Safe to call at any time, including when nothing is running.
  function invalidateCurrentRun (reason) {
    state.runToken++
    state.activeCancelSources.forEach(function (source) {
      source.cancel(reason)
    })
    state.activeCancelSources.clear()
  }

  // Bypasses/clears the cache for the very next runQueue pass only (auto-resets when that run
  // finishes) -- use for an explicit manual refresh.
  function setForceRefresh (value) {
    state.forceRefreshActive = !!value
  }

  return {
    buildCacheKey: buildCacheKey,
    fetchWithCache: fetchWithCache,
    runQueue: runQueue,
    invalidateCurrentRun: invalidateCurrentRun,
    setForceRefresh: setForceRefresh
  }
}

export default createApiRequestScheduler



// WEBPACK FOOTER //
// ./src/utils/apiRequestScheduler.js